import type { Components, Theme } from '@mui/material';
import { gradients } from './gradients';
import { glowShadows } from './shadows';
import { brand, neutrals } from './palette';

export const components: Components<Theme> = {
  MuiCssBaseline: {
    styleOverrides: `
      * {
        font-family: 'Poppins', sans-serif;
        letter-spacing: 0;
      }

      *::-webkit-scrollbar {
        width: 7px;
        height: 7px;
        background-color: gainsboro;
      }

      *::-webkit-scrollbar-thumb {
        background: ${gradients.primaryAlpha};
        border-radius: 1px;
      }

      body {
        margin: 0;
        background: ${neutrals.background};
      }

      a {
        text-decoration: none;
      }
    `,
  },

  MuiButton: {
    defaultProps: {
      disableElevation: true,
    },
    styleOverrides: {
      root: {
        borderRadius: 10,
        padding: '7px 20px',
        fontWeight: 500,
        fontSize: '0.875rem',
        textTransform: 'none' as const,
        transition: 'all 0.2s ease',
        '&:active': {
          transform: 'scale(0.97)',
        },
      },
      contained: {
        background: gradients.primary,
        color: brand.white,
        '&:hover': {
          background: gradients.primary,
          boxShadow: glowShadows.hoverGlow,
          filter: 'brightness(1.05)',
        },
      },
      outlined: {
        borderColor: brand.teal,
        color: brand.teal,
        '&:hover': {
          borderColor: brand.teal,
          backgroundColor: 'rgba(30, 190, 200, 0.04)',
          boxShadow: glowShadows.sm,
        },
      },
      text: {
        color: brand.teal,
        '&:hover': {
          backgroundColor: 'rgba(30, 190, 200, 0.04)',
        },
      },
    },
  },

  MuiCard: {
    defaultProps: {
      elevation: 0,
    },
    styleOverrides: {
      root: {
        borderRadius: 12,
        border: `1px solid ${neutrals.border}`,
        transition: 'transform 0.25s ease, box-shadow 0.25s ease',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: glowShadows.cardGlow,
        },
      },
    },
  },

  MuiTextField: {
    defaultProps: {
      variant: 'outlined',
      size: 'small',
    },
    styleOverrides: {
      root: {
        '& .MuiOutlinedInput-root': {
          borderRadius: 8,
          backgroundColor: neutrals.card,
          '& fieldset': {
            borderColor: neutrals.card,
          },
          '&:hover fieldset': {
            borderColor: neutrals.inputBorder,
          },
          '&.Mui-focused fieldset': {
            borderColor: brand.teal,
            boxShadow: glowShadows.focusGlow,
          },
        },
      },
    },
  },

  MuiAppBar: {
    defaultProps: {
      elevation: 0,
    },
    styleOverrides: {
      root: {
        background: 'rgba(255, 255, 255, 0.85)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        borderBottom: '1px solid rgba(221, 221, 221, 0.4)',
        color: neutrals.text,
      },
    },
  },

  MuiDrawer: {
    styleOverrides: {
      paper: {
        background: 'rgba(255, 255, 255, 0.9)',
        backdropFilter: 'blur(16px)',
        WebkitBackdropFilter: 'blur(16px)',
        borderRight: '1px solid rgba(221, 221, 221, 0.3)',
      },
    },
  },

  MuiDialog: {
    styleOverrides: {
      paper: {
        borderRadius: 16,
        background: 'rgba(255, 255, 255, 0.88)',
        backdropFilter: 'blur(24px)',
        WebkitBackdropFilter: 'blur(24px)',
        border: '1px solid rgba(255, 255, 255, 0.4)',
      },
    },
  },

  MuiChip: {
    styleOverrides: {
      root: {
        borderRadius: 8,
        fontWeight: 500,
      },
      colorPrimary: {
        background: gradients.primary,
        color: brand.white,
      },
    },
  },

  MuiBadge: {
    styleOverrides: {
      colorPrimary: {
        background: gradients.primary,
      },
    },
  },

  MuiTooltip: {
    styleOverrides: {
      tooltip: {
        backgroundColor: neutrals.text,
        fontSize: '0.75rem',
        borderRadius: 8,
        padding: '6px 12px',
      },
      popper: {
        zIndex: 999999,
      },
    },
  },

  MuiCircularProgress: {
    defaultProps: {
      size: 40,
    },
    styleOverrides: {
      colorPrimary: {
        color: brand.green,
      },
    },
  },

  MuiPaper: {
    defaultProps: {
      elevation: 0,
    },
    styleOverrides: {
      root: {
        borderRadius: 12,
      },
    },
  },

  MuiIconButton: {
    styleOverrides: {
      root: {
        transition: 'transform 0.2s ease',
        '&:hover': {
          transform: 'scale(1.15)',
        },
        '&:active': {
          transform: 'scale(0.95)',
        },
      },
    },
  },
};
